import React, {FC} from 'react';
import styles from './Stepper.module.scss';
import {StepConfig, WizardStep} from '../../types';
import {useWizardContext} from '../../context';
import {cn} from '../../../../../../utils';
import {ReactComponent as ArrowRight} from 'assets/images/svg/arrow-look-right-green.svg';
import StepApprovedIcon from 'assets/images/svg/step-approved.svg';
import StepRejectedIcon from 'assets/images/svg/step-rejected.svg';
import {StepStatus} from '../../../../../../api-client';
import {Image} from "../../../../../../components";

interface StepperProps {
  onStepClick?: (step: WizardStep) => void;
  allowFreeNavigation?: boolean;
}

const defaultSteps: StepConfig[] = [
  {id: WizardStep.START, label: 'pre-scoring'},
  {id: WizardStep.VERIFICATION, label: 'Верификация'},
  {id: WizardStep.SCORING, label: 'Скоринг'},
  {id: WizardStep.DOCUMENTS, label: 'Документы'},
];

const Stepper: FC<StepperProps> = ({ 
  onStepClick,
  allowFreeNavigation = true,
}) => {
  const {activeStep, setActiveStep, isStepCompleted, completedSteps, onboardingStepStatus, backendCurrentStep, isInitialDataLoaded} = useWizardContext();
  const stepperCurrentStep = backendCurrentStep ?? activeStep;

  const handleStepClick = (step: WizardStep, isDisabled: boolean) => {
    if (isDisabled) return;
    
    if (onStepClick) {
      onStepClick(step);
    } else {
      setActiveStep(step);
    }
  };

  const isStepDisabled = (step: WizardStep): boolean => {
    if (allowFreeNavigation) return false;
    if (isStepCompleted(step)) return false;
    if (step === WizardStep.START) return false;
    
    const previousStep = step - 1;
    return !completedSteps.includes(previousStep as WizardStep);
  };

  const stepClasses = (step: WizardStep): string => {
    const isActive = stepperCurrentStep === step;
    const isSelected = activeStep === step; // шаг, на который кликнули (текущий экран)
    const isCompleted = isStepCompleted(step);
    const isDisabled = isStepDisabled(step);
    const fromBackend = !isInitialDataLoaded || onboardingStepStatus != null;
    const isRejected = isActive && onboardingStepStatus === StepStatus.REJECTED;
    const isActiveGray = isActive && fromBackend && !isRejected;

    const isGrayCircle = isActiveGray || isDisabled || (!isActive && !isCompleted);
    const selectedRingModifier =
      isSelected && isRejected
        ? styles['step--selectedRejected']
        : isSelected && isCompleted
          ? styles['step--selectedCompleted']
          : isSelected && isGrayCircle
            ? styles['step--selectedActiveGray']
            : isSelected
              ? styles['step--selectedActive']
              : null;

    return cn(
      styles.step,
      isActive && styles['step--active'],
      isCompleted && !isActive && styles['step--completed'],
      isDisabled && styles['step--disabled'],
      isActiveGray && styles['step--activeGray'],
      isRejected && styles['step--rejected'],
      selectedRingModifier,
      !isDisabled && styles['step--pulse'],
    );
  };

  const renderStepCircleContent = (stepId: WizardStep): React.ReactNode => {
    const isActive = stepperCurrentStep === stepId;
    const isCompleted = isStepCompleted(stepId);
    const isRejected = isActive && onboardingStepStatus === StepStatus.REJECTED;

    if (isCompleted) return <Image src={StepApprovedIcon} className={styles.stepCircleIcon} aria-hidden alt={'icon'}/>;
    if (isRejected) return <Image src={StepRejectedIcon}  className={styles.stepCircleIcon} aria-hidden alt={'icon'}/>;
    return stepId;
  };

  const connectorClass = (stepIndex: number): string => {
    const step = defaultSteps[stepIndex];
    const isCompleted = isStepCompleted(step.id) || stepperCurrentStep > step.id;

    return cn(
      styles.connector,
      isCompleted && styles['connector--completed']
    );
  };

  const currentStepIndex = defaultSteps.findIndex(s => s.id === activeStep);
  const currentStepLabel = defaultSteps[currentStepIndex]?.label || '';
  const isFirstStep = activeStep === WizardStep.START;
  const isLastStep = activeStep === WizardStep.DOCUMENTS;

  const handlePrev = () => {
    if (!isFirstStep) {
      const prevStep = defaultSteps[currentStepIndex - 1];
      if (prevStep) {
        setActiveStep(prevStep.id);
      }
    }
  };

  const handleNext = () => {
    if (!isLastStep) {
      const nextStep = defaultSteps[currentStepIndex + 1];
      if (nextStep && !isStepDisabled(nextStep.id)) {
        setActiveStep(nextStep.id);
      }
    }
  };

  return (
    <>
      <div className={styles.stepper}>
        {defaultSteps.map((step, index) => (
          <div
            key={step.id}
            className={stepClasses(step.id)}
            onClick={() => handleStepClick(step.id, isStepDisabled(step.id))}
          >
            <div className={styles.stepCircleWrapper}>
              <div className={styles.stepCircle}>
                {renderStepCircleContent(step.id)}
              </div>
            </div>
            <div className={styles.stepLabelContainer}>
              <div className={styles.stepLabel}>{step.label}</div>
            </div>
            <div className={connectorClass(index)} />
          </div>
        ))}

        <div className={styles.step}>
          <div className={styles.stepCircleWrapper}>
            <div className={styles.stepCircle}>
              ₽
            </div>
          </div>
          <div className={styles.stepLabelContainer}>
            <div className={styles.stepLabel}>Финансирование</div>
          </div>
        </div>
      </div>

      <div className={styles.mobileStepper}>
        <button 
          className={cn(styles.navButton, styles.navButtonPrev, isFirstStep && styles.navButtonDisabled)}
          onClick={handlePrev}
          disabled={isFirstStep}
          aria-label="Предыдущий шаг"
        >
          <ArrowRight className={cn(styles.arrow, styles.arrowLeft)} />
        </button>

        <div className={styles.mobileStepInfo}>
          <div className={styles.mobileStepCircle}>
            {activeStep}
          </div>
          <div className={styles.mobileStepLabel}>{currentStepLabel}</div>
        </div>

        <button 
          className={cn(styles.navButton, styles.navButtonNext, isLastStep && styles.navButtonDisabled)}
          onClick={handleNext}
          disabled={isLastStep}
          aria-label="Следующий шаг"
        >
          <ArrowRight className={cn(styles.arrow, styles.arrowRight)} />
        </button>
      </div>
    </>
  );
};

export default Stepper;

